import React, { PropsWithChildren } from 'react'
import clsx from 'clsx'
import {
  Dots as DotsStyles,
  Spinner as SpinnerStyles,
  Progresscircle as ProgresscircleStyles,
} from '@airbus/elements/elements'
import useDeprecatedComponent from '../../lib/hooks/useDeprecatedComponent'
import useDeprecatedProps from '../../lib/hooks/useDeprecatedProps'
import { createUseStyles } from '../../lib/styling'
import { CommonProps } from '../helperTypes'

type Props = PropsWithChildren<{
  /** Three dots variant */
  dots?: boolean
  /** Spinner label */
  label?: string
  /** Percentage of the spinner */
  percentage?: number
  /** Show percentage text value */
  showPercentage?: boolean
  /**
   * Size of the spinner
   * @default small
   */
  size?: 'small' | 'medium'
}>

export type SpinnerProps = Props & CommonProps

const useStyles = createUseStyles(SpinnerStyles, 'spinner')
const useDotsStyles = createUseStyles(DotsStyles, 'dots')
const useProgresscircleStyles = createUseStyles(ProgresscircleStyles, 'determinate')

const Spinner = (props: SpinnerProps) => {
  const { children, className, dots, label, percentage = 0, showPercentage = false, size = 'small', ...rest } = props
  const classes = useStyles()
  const dotsClasses = useDotsStyles()
  const progresscircleClasses = useProgresscircleStyles()

  if (dots) {
    return (
      <div className={clsx(className, dotsClasses.root)} role='status' {...rest}>
        <div className={dotsClasses.item}></div>
        <div className={dotsClasses.item}></div>
        <div className={dotsClasses.item}></div>
      </div>
    )
  } else {
    return (
      <div
        className={clsx(
          className,
          percentage ? progresscircleClasses.root : classes.root,
          size === 'medium' ? (percentage ? progresscircleClasses._medium : classes._medium) : undefined,
        )}
        role='status'
        {...rest}
        style={percentage ? ({ '--percentage': percentage } as React.CSSProperties) : undefined}
      >
        {size === 'medium' && showPercentage && <div role='status'>{percentage}%</div>}
        {!showPercentage && children}
        {label && <div className={classes.label}>{label}</div>}
      </div>
    )
  }
}

export default Spinner

type DeprecatedProps = {
  small?: boolean
  fill?: 'block' | 'block-white'
}

type LoadingProps = SpinnerProps & DeprecatedProps

export const Loading = (props: LoadingProps) => {
  const { small, fill, ...rest } = props
  useDeprecatedComponent('Loading', 'Please use Spinner.')
  useDeprecatedProps(
    'Loading',
    {
      small: 'size',
      fill: undefined,
    },
    props,
  )
  return <Spinner {...rest} />
}
